import {
  forwardRef,
  ReactNode,
  FC,
  ForwardRefExoticComponent,
  RefAttributes,
} from "react";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import classnames from "classnames";
import CardHeader, { CardHeaderProps } from "./card-header";
import CardFooter, { CardFooterProps } from "./card-footer";
import { CardContextProvider } from "./card-context";

import "./card.scss";

type CardRef = HTMLElement;

type BorderPosition = "left" | "right" | "bottom" | "top" | "all";

export interface CardProps {
  ariaLabel?: string;
  border?: BorderPosition;
  children?: ReactNode;
  className?: string;
  id?: string;
  isHoverable?: boolean;
  isRaised?: boolean;
  isRounded?: boolean;
  isTransparent?: boolean;
}

export interface CompoundCardProps
  extends ForwardRefExoticComponent<CardProps & RefAttributes<CardRef>> {
  Header: FC<CardHeaderProps>;
  Footer: FC<CardFooterProps>;
}

const Card: CompoundCardProps = forwardRef<CardRef, CardProps>(
  (
    {
      className,
      isRounded = false,
      isTransparent = true,
      isRaised = false,
      isHoverable = false,
      ariaLabel,
      children,
      id,
      border,
    }: CardProps,
    ref,
  ) => {
    const getBemBlocksWith = createBemBlockBuilder(["eb-card", className]);

    const classNames = classnames(
      getBemBlocksWith(),
      border === "all" && getBemBlocksWith("--border"),
      border && border !== "all" && getBemBlocksWith(`--border-${border}`),
      isHoverable && getBemBlocksWith("--hoverable"),
      isRounded && border === "all" && getBemBlocksWith("--rounded"),
      isRaised && getBemBlocksWith("--raised"),
      isTransparent && getBemBlocksWith("--transparent"),
    );

    const cardContext = {
      getBemBlocksWith,
    };

    return (
      <CardContextProvider value={cardContext}>
        <section
          ref={ref}
          id={id}
          className={classNames}
          aria-label={ariaLabel}
          data-auto="card"
        >
          {children}
        </section>
      </CardContextProvider>
    );
  },
) as CompoundCardProps;

Card.Header = CardHeader;
Card.Header.displayName = "Card.Header";
Card.Footer = CardFooter;
Card.Footer.displayName = "Card.Footer";
Card.displayName = "Card";

export default Card;
